import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Avatar,
  useTheme,
  alpha
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  ContentCopy as ContentCopyIcon,
  FiberManualRecord as DotIcon
} from '@mui/icons-material';
import { formatDateSafe, toDate } from '../../../shared/utils/dateHelpers';
import { formatDistanceToNow } from 'date-fns';

interface ClientDetailsHeaderProps {
  client: {
    client_id: string;
    client_name: string;
    email: string;
    mobile_number: string;
    primary_location: string;
    lead_coach_name: string;
    visit_dates: string[];
    active_programme: boolean;
    programme_start_date?: string;
    programme_end_date?: string;
    latest_expiration_date?: string;
    conversion_status?: boolean;
    subsequent_spend?: number;
    current_programme?: string;
    join_date?: string;
  };
  onCopyClientId?: () => void;
}

// Helper function to reformat names
const formatName = (name: string | undefined): string => {
  if (!name) return 'N/A';
  if (name.includes(',')) {
    const [lastName, firstName] = name.split(',').map(part => part.trim());
    return `${firstName} ${lastName}`;
  }
  return name;
};

// Helper to calculate program progress
const calculateProgramProgress = (startDate?: string, endDate?: string): number => {
  if (!startDate || !endDate) return 0;

  const start = toDate(startDate);
  const end = toDate(endDate);
  const now = new Date();

  if (!start || !end) return 0;

  const total = end.getTime() - start.getTime();
  const elapsed = now.getTime() - start.getTime();

  return Math.min(Math.max((elapsed / total) * 100, 0), 100);
};

// Helper to calculate days until expiration
const getDaysUntilExpiration = (expirationDate?: string): number | null => {
  if (!expirationDate) return null;
  const expDate = toDate(expirationDate);
  if (!expDate) return null;

  const now = new Date();
  const diff = expDate.getTime() - now.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

// Helper to calculate client tenure
const getClientTenure = (joinDate?: string, firstSessionDate?: string): string => {
  const startDate = toDate(joinDate || firstSessionDate || '');
  if (!startDate) return 'N/A';

  return formatDistanceToNow(startDate, { addSuffix: false });
};

export const ClientDetailsHeader: React.FC<ClientDetailsHeaderProps> = ({
  client,
  onCopyClientId
}) => {
  const theme = useTheme();
  const sessionCount = client.visit_dates?.length || 0;
  const programProgress = calculateProgramProgress(client.programme_start_date, client.latest_expiration_date);
  const daysUntilExpiration = getDaysUntilExpiration(client.latest_expiration_date);
  const tenure = getClientTenure(client.join_date, client.visit_dates?.[0]);

  const lastSessionDate = client.visit_dates && client.visit_dates.length > 0
    ? toDate(client.visit_dates[client.visit_dates.length - 1])
    : null;

  const latestSessionText = lastSessionDate
    ? formatDistanceToNow(lastSessionDate, { addSuffix: true })
    : 'No sessions';

  const handleCopyClientId = () => {
    navigator.clipboard.writeText(client.client_id);
    onCopyClientId?.();
  };

  // Determine status
  const isExpired = daysUntilExpiration !== null && daysUntilExpiration < 0;
  const isExpiringSoon = daysUntilExpiration !== null && daysUntilExpiration > 0 && daysUntilExpiration <= 7;

  const statusColor = isExpired
    ? theme.palette.error.main
    : isExpiringSoon
    ? theme.palette.warning.main
    : client.active_programme
    ? theme.palette.success.main
    : theme.palette.grey[400];

  const statusText = isExpired
    ? 'Expired'
    : isExpiringSoon
    ? 'Expiring Soon'
    : client.active_programme
    ? 'Active'
    : 'Inactive';

  return (
    <Box
      sx={{
        mb: 3,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        borderRadius: 2,
        background: theme.palette.mode === 'dark'
          ? alpha(theme.palette.background.paper, 0.6)
          : theme.palette.background.paper,
        backdropFilter: 'blur(10px)',
        overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      {/* Top Section: Avatar, Name, Status, Actions */}
      <Box sx={{
        p: 3,
        pb: 2,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 2
      }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flex: 1, minWidth: 0 }}>
          {/* Avatar with status ring */}
          <Box sx={{ position: 'relative' }}>
            <Box
              sx={{
                position: 'absolute',
                inset: -3,
                borderRadius: '50%',
                background: `conic-gradient(${statusColor} ${programProgress}%, ${alpha(statusColor, 0.1)} ${programProgress}%)`,
                padding: '3px',
                display: client.active_programme && programProgress > 0 ? 'block' : 'none'
              }}
            />
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: alpha(statusColor, 0.15),
                color: statusColor,
                fontSize: '1.5rem',
                fontWeight: 600,
                border: `3px solid ${theme.palette.background.paper}`,
                position: 'relative'
              }}
            >
              {formatName(client.client_name).split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
            </Avatar>
          </Box>

          {/* Name and Status */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 0.5,
                letterSpacing: '-0.02em',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {formatName(client.client_name)}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <DotIcon sx={{ fontSize: 12, color: statusColor }} />
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: statusColor
                  }}
                >
                  {statusText}
                </Typography>
              </Box>
              {client.current_programme && (
                <>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>·</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    {client.current_programme}
                  </Typography>
                </>
              )}
              {daysUntilExpiration !== null && daysUntilExpiration > 0 && (
                <>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>·</Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: daysUntilExpiration <= 7 ? 'warning.main' : 'text.secondary',
                      fontWeight: 500
                    }}
                  >
                    {daysUntilExpiration} days left
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        </Box>

        {/* Contact Actions - Minimal */}
        {/*
          SIZING ADJUSTMENT GUIDE:
          - Button size: Change 'width' and 'height' in sx (current: 40x40)
          - Icon size: Change fontSize prop (options: 'small', 'medium', 'large', or custom number)
          - Gap between buttons: Change 'gap' value (current: 1 = 8px)
        */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {client.mobile_number && (
            <Tooltip title="WhatsApp" arrow>
              <IconButton
                component="a"
                href={`https://wa.me/${client.mobile_number.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  width: 40,
                  height: 40,
                  color: '#25D366',
                  '&:hover': {
                    backgroundColor: alpha('#25D366', 0.08)
                  }
                }}
              >
                <WhatsAppIcon fontSize="large" />
              </IconButton>
            </Tooltip>
          )}

          {client.email && (
            <Tooltip title="Email" arrow>
              <IconButton
                component="a"
                href={`mailto:${client.email}`}
                sx={{
                  width: 40,
                  height: 40,
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08)
                  }
                }}
              >
                <EmailIcon fontSize="large" />
              </IconButton>
            </Tooltip>
          )}

          {client.mobile_number && (
            <Tooltip title="Call" arrow>
              <IconButton
                component="a"
                href={`tel:${client.mobile_number}`}
                sx={{
                  width: 40,
                  height: 40,
                  color: theme.palette.text.secondary,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.text.secondary, 0.08)
                  }
                }}
              >
                <PhoneIcon fontSize="large" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Data Grid - Compact Metrics */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(3, 1fr)',
            md: 'repeat(5, 1fr)'
          },
          gap: 3,
          backgroundColor: alpha(theme.palette.action.hover, 0.02)
        }}
      >
        {/* Client ID */}
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontSize: '0.65rem',
              fontWeight: 600,
              display: 'block',
              mb: 0.5
            }}
          >
            Client ID
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'ui-monospace, "SF Mono", Monaco, "Cascadia Code", monospace',
                fontWeight: 600,
                fontSize: '0.95rem',
                color: 'text.primary'
              }}
            >
              {client.client_id}
            </Typography>
            <Tooltip title="Copy ID" arrow>
              <IconButton
                size="small"
                onClick={handleCopyClientId}
                sx={{
                  padding: 0.25,
                  opacity: 0.5,
                  '&:hover': { opacity: 1 }
                }}
              >
                <ContentCopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Latest Session */}
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontSize: '0.65rem',
              fontWeight: 600,
              display: 'block',
              mb: 0.5
            }}
          >
            Last Session
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              fontSize: '0.95rem',
              color: 'text.primary'
            }}
          >
            {latestSessionText}
          </Typography>
        </Box>

        {/* Location */}
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontSize: '0.65rem',
              fontWeight: 600,
              display: 'block',
              mb: 0.5
            }}
          >
            Location
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              fontSize: '0.95rem',
              color: 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {client.primary_location || 'N/A'}
          </Typography>
        </Box>

        {/* Conversion */}
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontSize: '0.65rem',
              fontWeight: 600,
              display: 'block',
              mb: 0.5
            }}
          >
            Converted
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              fontSize: '0.95rem',
              color: client.conversion_status ? 'success.main' : 'text.secondary'
            }}
          >
            {client.conversion_status ? '✓ Yes' : '✗ No'}
          </Typography>
        </Box>

        {/* Spend */}
        <Box>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontSize: '0.65rem',
              fontWeight: 600,
              display: 'block',
              mb: 0.5
            }}
          >
            Total Spend
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontFamily: 'ui-monospace, "SF Mono", Monaco, "Cascadia Code", monospace',
              fontWeight: 600,
              fontSize: '0.95rem',
              color: client.subsequent_spend && client.subsequent_spend > 1000
                ? 'success.main'
                : 'text.primary'
            }}
          >
            {client.subsequent_spend !== undefined && client.subsequent_spend !== null
              ? `$${client.subsequent_spend.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : 'N/A'}
          </Typography>
        </Box>
      </Box>

      {/* Bottom Section: Progress + Metadata */}
      <Box sx={{ px: 3, py: 2 }}>
        {/* Progress Bar */}
        {client.active_programme && programProgress > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Box
              sx={{
                height: 4,
                borderRadius: 2,
                backgroundColor: alpha(statusColor, 0.1),
                overflow: 'hidden',
                position: 'relative'
              }}
            >
              <Box
                sx={{
                  height: '100%',
                  width: `${programProgress}%`,
                  backgroundColor: statusColor,
                  transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                  borderRadius: 2
                }}
              />
            </Box>
          </Box>
        )}

        {/* Metadata Row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontSize: '0.75rem'
            }}
          >
            <strong style={{ color: theme.palette.text.primary, fontWeight: 600 }}>{sessionCount}</strong> sessions
          </Typography>
          {tenure !== 'N/A' && (
            <>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>•</Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.75rem'
                }}
              >
                Member for <strong style={{ color: theme.palette.text.primary, fontWeight: 600 }}>{tenure}</strong>
              </Typography>
            </>
          )}
          {client.lead_coach_name && (
            <>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>•</Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.75rem'
                }}
              >
                Coach: <strong style={{ color: theme.palette.text.primary, fontWeight: 600 }}>{formatName(client.lead_coach_name)}</strong>
              </Typography>
            </>
          )}
          {client.active_programme && programProgress > 0 && (
            <>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>•</Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.75rem'
                }}
              >
                <strong style={{ color: theme.palette.text.primary, fontWeight: 600 }}>{Math.round(programProgress)}%</strong> complete
              </Typography>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};
